import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Toast } from "primereact/toast";
import { Card } from "primereact/card";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";

import { useBreadcrumb } from "sj-ui-toolkit/src/atlas/hooks/useBreadcrumbs";
import { createApiKey, deleteApiKey, getApiKeys } from "sj-ui-toolkit/src/api/admin/settings";
import { randomString } from "sj-ui-toolkit/src/utils";
import { AdminAppRoutes } from "sj-ui-toolkit/src/constants/routes";

const REPORTING_KEY_LENGTH = 32;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const ReportingApiConnection = () => {
  useBreadcrumb({
    label: "Reporting API",
    route: AdminAppRoutes.routes.connections.routes.reportingApi.route,
    display: "page",
  });

  const toast = useRef<Toast>(null);
  const [apiKeys, setApiKeys] = useState<{ id: number; name: string }[]>([]);
  const [newKeyValue, setNewKeyValue] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
  const [keyName, setKeyName] = useState("");

  const showLocalToast = useCallback(
    (
      severity: "success" | "info" | "warn" | "error",
      summary: string,
      detail: string,
      life = 3000
    ) => {
      toast.current?.show({ severity, summary, detail, life });
    },
    []
  );

  const loadKeys = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await getApiKeys();
      setApiKeys(data?.apiKeys ?? []);
    } catch {
      showLocalToast("error", "Error", "Failed to load Reporting API keys.", 5000);
    } finally {
      setIsLoading(false);
    }
  }, [showLocalToast]);

  useEffect(() => {
    loadKeys();
  }, [loadKeys]);

  const sortedKeys = useMemo(
    () => [...apiKeys].sort((a, b) => (a.name || "").localeCompare(b.name || "")),
    [apiKeys]
  );

  const handleGenerate = async () => {
    if (!keyName.trim()) return;

    setIsLoading(true);
    try {
      const generated = randomString(REPORTING_KEY_LENGTH);
      const data = await createApiKey(keyName.trim(), generated);
      setApiKeys(prev => [...prev, data.apiKey]);
      setNewKeyValue(generated);
      setKeyName("");
      setIsCreateModalVisible(false);
      showLocalToast("success", "Success", "Reporting API key created successfully");
    } catch {
      showLocalToast("error", "Error", "Failed to create Reporting API key. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopy = (text: string) => {
    if (text) {
      navigator.clipboard.writeText(text);
      showLocalToast("info", "Copied", "API key copied to clipboard");
    }
  };

  const confirmDelete = (apiKey: { id: number; name: string }) => {
    confirmDialog({
      message: `Are you sure you want to delete "${apiKey.name}"? This action cannot be undone.`,
      header: "Delete Reporting API Key",
      icon: "pi pi-exclamation-triangle",
      defaultFocus: "reject",
      acceptClassName: "p-button-danger",
      accept: async () => {
        try {
          await deleteApiKey(apiKey.id);
          setApiKeys(prev => prev.filter(k => k.id !== apiKey.id));
          showLocalToast("info", "Deleted", "Reporting API key deleted successfully");
        } catch {
          showLocalToast("error", "Error", "Failed to delete Reporting API key");
        }
      },
    });
  };

  return (
    <>
      <Toast ref={toast} />
      <ConfirmDialog />

      <div className="mb-4">
        <h1>Reporting API</h1>
      </div>

      <Card>
        <div className="flex justify-between items-center">
          <div>
            <strong>Reporting API Keys</strong>
            <p className="m-0 text-secondary">Create and manage keys for reporting integrations.</p>
          </div>
          <Button
            label="Create Reporting API Key"
            icon="pi pi-plus"
            onClick={() => setIsCreateModalVisible(true)}
            disabled={isLoading}
          />
        </div>

        {newKeyValue && (
          <div className="mt-4">
            <strong>New Reporting API Key (copy this now - it will not be shown again):</strong>
            <div className="p-inputgroup mt-2">
              <InputText value={newKeyValue} readOnly />
              <Button
                icon="pi pi-copy"
                onClick={() => handleCopy(newKeyValue)}
                tooltip="Copy to clipboard"
                tooltipOptions={{ position: "top", className: "whitespace-nowrap" }}
              />
              <Button
                icon="pi pi-times"
                onClick={() => setNewKeyValue("")}
                className="p-button-secondary"
                tooltip="Dismiss"
                tooltipOptions={{ position: "top", className: "whitespace-nowrap" }}
              />
            </div>
          </div>
        )}

        <div className="mt-4">
          <DataTable
            value={sortedKeys}
            className="p-datatable-sm"
            tableClassName="dt-responsive-text"
            sortMode="single"
            loading={isLoading}
          >
            <Column field="name" header="Name" sortable />
            <Column
              header="Action"
              body={(rowData: { id: number; name: string }) => (
                <Button
                  label="Delete"
                  severity="danger"
                  text
                  size="small"
                  disabled={isLoading}
                  onClick={() => confirmDelete(rowData)}
                />
              )}
            />
          </DataTable>
          {!isLoading && sortedKeys.length === 0 && (
            <p className="text-secondary mt-3">
              No Reporting API keys found. Create one to get started.
            </p>
          )}
        </div>
      </Card>

      <Dialog
        header="Create Reporting API Key"
        visible={isCreateModalVisible}
        onHide={() => {
          setIsCreateModalVisible(false);
          setKeyName("");
        }}
        style={{ width: "40rem", maxWidth: "95vw" }}
        modal
        footer={
          <div className="flex gap-2 justify-end">
            <Button
              label="Cancel"
              className="p-button-secondary"
              onClick={() => {
                setIsCreateModalVisible(false);
                setKeyName("");
              }}
              disabled={isLoading}
            />
            <Button
              label="Create"
              icon="pi pi-check"
              onClick={handleGenerate}
              disabled={isLoading || !keyName.trim()}
            />
          </div>
        }
      >
        <InputText
          value={keyName}
          onChange={e => setKeyName(e.target.value)}
          placeholder="Key Name (e.g., Reporting Dashboard)"
          className="w-full"
          maxLength={120}
          disabled={isLoading}
          autoFocus
        />
      </Dialog>
    </>
  );
};

export default ReportingApiConnection;
